import React, { useState } from 'react';
import styles from '../../Patient.module.css';
import { useSelector, useDispatch } from 'react-redux';
import inputStyles from '../../../ManagementPages.module.css';
import { setFormField } from '../../../../slicers/PatientSlice';
import { customStyles } from '../../../../components/AccountManagement/CustomStyles';
import Select from 'react-select';

/** Same validation as EmergencyContact.js: at least one of phone/email; if phone then 10 digits + country code; zip 5 digits if provided. */
const validateEmergencyContact = (data) => {
    const errors = {};
    const hasPhone = (data.phone || "").trim() !== "";
    const hasEmail = (data.email || "").trim() !== "";

    if (!hasPhone && !hasEmail) {
        errors.phone = "Please provide either a phone number or email address.";
        errors.email = "Please provide either a phone number or email address.";
    }
    if (hasPhone) {
        if (!/^\d{10}$/.test(String(data.phone).trim())) {
            errors.phone = "Please provide a valid phone number; phone numbers must contain only numbers and be 10 digits in length.";
        }
        if (!(data.countryCode || "").trim()) {
            errors.countryCode = "Please provide a valid country code.";
        }
    }
    const zip = (data.zip || "").trim();
    if (zip && !/^\d{5}$/.test(zip)) {
        errors.zip = "Please provide a valid postal code; postal codes must contain only numbers and be 5 digits in length.";
    }
    return { valid: Object.keys(errors).length === 0, errors };
};

const EmergencyContactDetail = ({ setModal, setUpdateFn, setCancelFn }) => {

    const dispatch = useDispatch();
    const dropdowns = useSelector((state) => state.dropdowns);
    const patient = useSelector((state)=>state.patient);
    const {emergencyContact, firstName, lastName} = patient;
    const [showUpdateButtons, setShowUpdateButtons] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const initialState = {
        fullName: emergencyContact.fullName || "",
        relation: emergencyContact.relation || "",
        gender: emergencyContact.gender || "",
        countryCode: emergencyContact.countryCode || "",
        phone: emergencyContact.phone || "",
        email: emergencyContact.email || "",
        address1: emergencyContact.address1 || "",
        address2: emergencyContact.address2 || "",
        city: emergencyContact.city || "",
        state: emergencyContact.state || "",
        zip: emergencyContact.zip || "",
        country: emergencyContact.country || ""
    };
    const [ updatedInfo, setUpdatedInfo ] = useState(initialState);

    const [displayPhoneCountryCode, setDisplayPhoneCountryCode] = useState(() => {
        const countryCodeMatch = dropdowns.countries.find((code)=>code.phoneCountryCode === updatedInfo.countryCode)
        if (countryCodeMatch && updatedInfo.countryCode !== "") {
            return {value: countryCodeMatch.phoneCountryCode, label: `${countryCodeMatch.phoneCountryCode} (${countryCodeMatch.name})`}
        } else return {value:'', label: ''}
    });

    const [displayCountry, setDisplayCountry] = useState(() => {
        const selectedCountry = dropdowns.countries.find(country => country.code === updatedInfo.country);
        return selectedCountry ? { value: selectedCountry.code, label: selectedCountry.name } : { value: '', label: '' };
    });

    const [displayState, setDisplayState] = useState(() => {
        const selectedCountry = dropdowns.countries.find(country => country.code === updatedInfo.country);
        const selectedState = selectedCountry?.states.find(state => state.code === updatedInfo.state);
        return selectedState ? { value: selectedState.code, label: selectedState.name } : { value: '', label: '' };
    });

    const handleState = (selected) => {
        handleDropdownChange("state", selected.value);
        setDisplayState(selected);
    };

    const handleCountry = (selected) => {
        handleDropdownChange("country", selected.value);
        handleDropdownChange("state", "");
        setDisplayState({ value: '', label: '' })
        setDisplayCountry(selected);
    };

    const handlePhoneCountryCode = (selected) => {
        handleDropdownChange('countryCode', selected.value);
        setDisplayPhoneCountryCode(selected);
    };

    const handleDropdownChange = (id, value) => {
        const tempData = {...updatedInfo};
        tempData[id] = value;
        setUpdatedInfo(tempData);
        if (!showUpdateButtons) {
            setShowUpdateButtons(true);
        }
    };

    const handleChange = (e) => {
        const { id, value } = e.target;
        const slicedId = id.substring(10);
        const tempData = {...updatedInfo};
        tempData[slicedId] = value;
        setUpdatedInfo(tempData);
        if (!showUpdateButtons) {
            setShowUpdateButtons(true);
        }
    };

    const handleUpdate = () => {
        const { valid, errors } = validateEmergencyContact(updatedInfo);
        if (!valid) {
            setValidationErrors(errors);
            return;
        }
        setValidationErrors({});
        const updatingInfo = { ...patient, emergencyContact: updatedInfo };
        const updateFn = () => {
            setShowUpdateButtons(false);
            dispatch(setFormField({ key: "emergencyContact", value: updatedInfo }));
        };
        setUpdateFn(() => updateFn);
        setCancelFn(() => handleCancel);
        setModal({ title: "Emergency Contact", message: `Update ${firstName} ${lastName}'s emergency contact?`, data: updatingInfo });
    };

    const handleCancel = () => {
        setUpdatedInfo(initialState);
        setShowUpdateButtons(false);
        setValidationErrors({});

        const countryCodeMatch = dropdowns.countries.find(
            (code) => code.phoneCountryCode === initialState.countryCode
        );
        if (countryCodeMatch && initialState.countryCode !== "") {
            setDisplayPhoneCountryCode({
                value: countryCodeMatch.phoneCountryCode,
                label: `${countryCodeMatch.phoneCountryCode} (${countryCodeMatch.name})`
            });
        } else {
            setDisplayPhoneCountryCode({ value: '', label: '' });
        }

        const selectedCountry = dropdowns.countries.find(country => country.code === initialState.country);
        const oldCountryVal =  selectedCountry ? { value: selectedCountry.code, label: selectedCountry.name } : { value: '', label: '' };
        setDisplayCountry(oldCountryVal);

        const selectedState = selectedCountry?.states.find(state => state.code === initialState.state);
        const oldStateVal = selectedState ? { value: selectedState.code, label: selectedState.name } : { value: '', label: '' };
        setDisplayState(oldStateVal);
    };

    return (
        <div style={{display:"flex", flexDirection:"column", gap:"15px"}}>
            <div className={inputStyles["styled-input-container"]}>
                <input
                    id="emergency_fullName"
                    style={{width:"300px"}}
                    value={updatedInfo.fullName}
                    onChange={handleChange}
                ></input>
                <label className={updatedInfo.fullName && inputStyles['filled']}>
                    Emergency Contact Full Name
                </label>
            </div>
            <div style={{position: "relative"}}>
                <span className="floating-label">Relationship</span>
                <select
                    className={inputStyles["styled-input-select"]}
                    id="emergency_relation"
                    value={updatedInfo.relation}
                    style={{width:"300px"}}
                    onChange={handleChange}
                >
                    <option value="">Select a Relationship</option>
                    {dropdowns.relationship.values.map((type) => (
                        <option key={type.id} value={type.id}>
                            {type.display}
                        </option>
                    ))}
                </select>
            </div>
            <div style={{position: "relative"}}>
                <span className="floating-label">Gender</span>
                <select
                    className={inputStyles["styled-input-select"]}
                    id="emergency_gender"
                    value={updatedInfo.gender}
                    style={{width:"300px"}}
                    onChange={handleChange}
                >
                    <option value="">Select a Gender</option>
                    {dropdowns.gender.values.map((type) => (
                        <option key={type.id} value={type.id}>
                            {type.display}
                        </option>
                    ))}
                </select>
            </div>
            <div className={inputStyles["styled-input-container"]}>
                <input
                    id="emergency_email"
                    type="email"
                    style={{ width: "300px", borderColor: validationErrors.email ? '#dc3545' : undefined }}
                    value={updatedInfo.email}
                    onChange={handleChange}
                ></input>
                <label className={updatedInfo.email && inputStyles['filled']}>
                    Email
                </label>
            </div>
            {validationErrors.email && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '-8px' }}>{validationErrors.email}</div>
            )}
            <div style={{display:"flex",width: "300px",justifyContent:"space-between", height:"44px"}}>
                <div style={{position: "relative", width: "125px"}}>
                    <span className={"floating-label"} style={{zIndex:"1"}}>Country Code*</span>
                    <Select
                         options={[...dropdowns.countries]
                            .sort((a, b) => {
                                // If both countries have +1, sort by name
                                if (a.phoneCountryCode === "+1" && b.phoneCountryCode === "+1") {
                                    // Prioritize United States specifically
                                    if (a.name === "United States") return -1;
                                    if (b.name === "United States") return 1;
                                    return a.name.localeCompare(b.name);
                                }
                                // Put +1 countries at the top
                                if (a.phoneCountryCode === "+1") return -1;
                                if (b.phoneCountryCode === "+1") return 1;
                                // Then sort alphabetically by name
                                return a.name.localeCompare(b.name);
                            })
                            .map((code) => ({
                                label: `${code.phoneCountryCode} (${code.name})`,
                                value: code.phoneCountryCode
                            }))}
                        styles={{
                            ...customStyles.popup,
                            container: (provided) => ({
                                ...provided,
                                width: '100%',
                            }),
                            control: (provided) => ({
                                ...provided,
                                height: '44px',
                                borderColor: validationErrors.countryCode ? '#dc3545' : "#686869",
                            }),
                        }}
                        value={displayPhoneCountryCode}
                        placeholder='Code*'
                        isSearchable={true}
                        onChange={handlePhoneCountryCode}
                    />
                    {validationErrors.countryCode && (
                        <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '2px' }}>{validationErrors.countryCode}</div>
                    )}
                </div>
                <div className={inputStyles["styled-input-container"]}>
                    <input
                        style={{ width: "170px", borderColor: validationErrors.phone ? '#dc3545' : undefined }}
                        id="emergency_phone"
                        value={updatedInfo.phone}
                        onChange={handleChange}
                        type="text"
                        pattern="^\d{10}$"
                    ></input>
                    <label className={updatedInfo.phone && inputStyles['filled']}>
                        Phone Number
                    </label>
                </div>
            </div>
            {validationErrors.phone && (
                <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '-8px' }}>{validationErrors.phone}</div>
            )}
            <div className={inputStyles["styled-input-container"]}>
                <input
                    id="emergency_address1"
                    value={updatedInfo.address1}
                    onChange={handleChange}
                    style={{width:"300px"}}
                ></input>
                <label className={updatedInfo.address1 && inputStyles['filled']}>
                    Street Address (Ave, St, Blvd, etc.)
                </label>
            </div>
            <div className={inputStyles["styled-input-container"]}>
                <input
                    id="emergency_address2"
                    style={{width:"300px"}}
                    value={updatedInfo.address2}
                    onChange={handleChange}
                ></input>
                <label className={updatedInfo.address2 && inputStyles['filled']}>
                Apartment, Suite, etc
                </label>
            </div>
            <div style={{display:"flex", flexDirection:"column", width: "300px"}}>
                <div className="input-block">
                    <div style={{position: "relative", width: "147px"}}>
                        <span className={"floating-label"} style={{zIndex:"1"}}>Country</span>
                            <Select
                                options={[...dropdowns.countries]
                                    .sort((a, b) => {
                                        // Prioritize United States
                                        if (a.name === "United States") return -1;
                                        if (b.name === "United States") return 1;
                                        // Then sort alphabetically by name
                                        return a.name.localeCompare(b.name);
                                    })
                                    .map((country) => ({
                                        label: country.name,
                                        value: country.code
                                    }))}
                                styles={{
                                    ...customStyles.popup,
                                    container: (provided) => ({
                                        ...provided,
                                        width: '100%',
                                    }),
                                }}
                                value={displayCountry}
                                placeholder='Country'
                                isSearchable={true}
                                onChange={handleCountry}
                            />
                    </div>
                    <div style={{position: "relative", width: "147px"}}>
                        <span className={"floating-label"} style={{zIndex:"1"}}>State/Province</span>
                        <Select
                            options={
                                displayCountry.value
                                    ? dropdowns.countries
                                            .find(country => country.code === displayCountry.value)
                                            ?.states.map(state => ({
                                                label: state.name,
                                                value: state.code
                                            })) || []
                                    : []
                            }
                            styles={{
                                ...customStyles.popup,
                                container: (provided) => ({
                                    ...provided,
                                    width: '100%',
                                }),
                            }}
                            value={displayState}
                            placeholder='State'
                            isSearchable={true}
                            onChange={handleState}
                        />
                    </div>
                </div>
                <div className="input-block" style={{marginBottom:"0px"}}>
                    <div className={inputStyles["styled-input-container"]}>
                        <input
                            id="emergency_city"
                            type="text"
                            style={{width:"147px"}}
                            value={updatedInfo.city}
                            onChange={handleChange}
                        ></input>
                        <label className={updatedInfo.city && inputStyles['filled']}>
                        City
                        </label>
                    </div>
                    <div className={inputStyles["styled-input-container"]}>
                        <input
                            style={{ width: "147px", borderColor: validationErrors.zip ? '#dc3545' : undefined }}
                            type="text"
                            inputMode="numeric"
                            id="emergency_zip"
                            value={updatedInfo.zip}
                            onChange={handleChange}
                        ></input>
                        <label className={updatedInfo.zip && inputStyles['filled']}>
                        Postal Code
                        </label>
                    </div>
                </div>
                {validationErrors.zip && (
                    <div style={{ color: '#dc3545', fontSize: '12px', marginTop: '2px' }}>{validationErrors.zip}</div>
                )}
            </div>
            <div style={{display:"flex", columnGap:"10px"}}>
                <button variant='secondary' disabled={!showUpdateButtons} className={styles.SecondaryButton} onClick={handleCancel} >Cancel</button>
                <button variant='primary' disabled={!showUpdateButtons} className={styles.ModelButton} onClick={handleUpdate} >Save</button>
            </div>
        </div>
    );
}

export default EmergencyContactDetail;